import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Alert,
  Skeleton,
  Button,
} from '@mui/material';
import {
  Person as PersonIcon,
  Warning as WarningIcon,
  TrendingDown as TrendingDownIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

interface TeacherAttendanceData {
  overall_attendance_rate: number;
  chronic_absenteeism_rate: number;
  impact_on_learning: {
    impact_level: string;
    estimated_learning_loss_percent: number;
  };
  attendance_by_day: {
    [key: string]: number;
  };
}

const TeacherAttendanceSummary: React.FC = () => {
  const { data, isLoading, error } = useQuery<{ data: TeacherAttendanceData }>({
    queryKey: ['teacher-attendance-analytics'],
    queryFn: async () => {
      // Try both token names for compatibility
      const token = localStorage.getItem('token') || localStorage.getItem('emis_auth_token');
      console.log('Fetching REAL teacher attendance from SIS...', { API_URL, token: token ? 'present' : 'missing' });
      const response = await axios.get(`${API_URL}/sis-integration/real-teacher-attendance/`, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });
      console.log('Teacher attendance response:', response.data);
      return response.data;
    },
    retry: 3,
    retryDelay: 1000,
  });

  if (isLoading) {
    return (
      <Box>
        <Skeleton variant="rectangular" height={60} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={60} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={60} />
      </Box>
    );
  }

  if (error || !data) {
    console.error('Teacher attendance error:', error);
    return (
      <Alert severity="error">
        Failed to load teacher attendance data
      </Alert>
    );
  }

  const analytics = data.data;

  const getImpactColor = (level: string) => {
    switch (level) {
      case 'minimal':
        return 'success';
      case 'low':
        return 'info';
      case 'moderate':
        return 'warning';
      case 'high':
        return 'error';
      default:
        return 'default';
    }
  };

  // Get best and worst attendance days
  const dayEntries = Object.entries(analytics.attendance_by_day);
  const bestDay = dayEntries.reduce((a, b) => a[1] > b[1] ? a : b);
  const worstDay = dayEntries.reduce((a, b) => a[1] < b[1] ? a : b);

  return (
    <Box sx={{ height: '100%', overflow: 'auto' }}>
      {/* Overall Attendance */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ pb: '16px !important' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <PersonIcon sx={{ mr: 1, color: 'primary.main', fontSize: 20 }} />
            <Typography variant="subtitle2" color="text.secondary">
              Overall Attendance
            </Typography>
          </Box>
          <Typography variant="h4" color="primary.main">
            {analytics.overall_attendance_rate}%
          </Typography>
          <LinearProgress
            variant="determinate"
            value={analytics.overall_attendance_rate}
            sx={{ mt: 1 }}
          />
        </CardContent>
      </Card>

      {/* Chronic Absenteeism */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ pb: '16px !important' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <WarningIcon sx={{ mr: 1, color: 'warning.main', fontSize: 20 }} />
            <Typography variant="subtitle2" color="text.secondary">
              Chronic Absenteeism
            </Typography>
          </Box>
          <Typography variant="h4" color="warning.main">
            {analytics.chronic_absenteeism_rate}%
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Teachers absent &gt;10% of time
          </Typography>
        </CardContent>
      </Card>

      {/* Learning Impact */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ pb: '16px !important' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <TrendingDownIcon sx={{ mr: 1, color: 'error.main', fontSize: 20 }} />
            <Typography variant="subtitle2" color="text.secondary">
              Learning Impact
            </Typography>
          </Box>
          <Typography variant="h4" color="error.main">
            {analytics.impact_on_learning.estimated_learning_loss_percent}%
          </Typography>
          <Chip
            label={analytics.impact_on_learning.impact_level.toUpperCase()}
            size="small"
            color={getImpactColor(analytics.impact_on_learning.impact_level) as any}
            sx={{ mt: 1 }}
          />
        </CardContent>
      </Card>

      {/* Weekly Pattern Summary */}
      <Card>
        <CardContent sx={{ pb: '16px !important' }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Weekly Pattern
          </Typography>
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" color="success.main">
              Best: {bestDay[0]} ({bestDay[1]}%)
            </Typography>
            <Typography variant="body2" color="error.main">
              Worst: {worstDay[0]} ({worstDay[1]}%)
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={((bestDay[1] - worstDay[1]) / bestDay[1]) * 100}
            color="warning"
            sx={{ mt: 1 }}
          />
          <Typography variant="caption" color="text.secondary">
            {(bestDay[1] - worstDay[1]).toFixed(1)}% daily variation
          </Typography>
        </CardContent>
      </Card>

      {/* View Details Button */}
      <Box sx={{ mt: 2, textAlign: 'center' }}>
        <Button
          variant="outlined"
          size="small"
          onClick={() => window.open('/analytics/teacher-attendance', '_blank')}
        >
          View Full Analytics
        </Button>
      </Box>
    </Box>
  );
};

export default TeacherAttendanceSummary;